/**
 * Circuit Breaker パターンの実装
 * 連続する失敗を監視し、一定期間リクエストを遮断してシステムを保護する
 */

export enum CircuitBreakerState {
  CLOSED = 'CLOSED', // 正常動作状態
  OPEN = 'OPEN', // 遮断状態
  HALF_OPEN = 'HALF_OPEN', // 部分復旧テスト状態
}

export interface CircuitBreakerConfig {
  failureThreshold: number; // 失敗回数の閾値
  recoveryTimeout: number; // 復旧試行までの待機時間(ms)
  monitoringPeriod: number; // 監視期間(ms)
  successThreshold: number; // HALF_OPEN状態での必要成功回数
}

export interface CircuitBreakerStats {
  state: CircuitBreakerState;
  failureCount: number;
  successCount: number;
  lastFailureTime?: number;
  lastSuccessTime?: number;
  totalCalls: number;
  totalFailures: number;
  totalSuccesses: number;
}

export class CircuitBreaker {
  private state: CircuitBreakerState = CircuitBreakerState.CLOSED;
  private failureCount: number = 0;
  private successCount: number = 0;
  private lastFailureTime?: number;
  private lastSuccessTime?: number;
  private totalCalls: number = 0;
  private totalFailures: number = 0;
  private totalSuccesses: number = 0;

  constructor(private config: CircuitBreakerConfig) {}

  /**
   * サービス呼び出しを実行
   */
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === CircuitBreakerState.OPEN) {
      if (this.canAttemptReset()) {
        this.state = CircuitBreakerState.HALF_OPEN;
        this.successCount = 0;
        console.log('🔄 Circuit Breaker: HALF_OPEN状態に移行');
      } else {
        const waitTime = this.getRemainingWaitTime();
        throw new Error(`Circuit Breaker is OPEN. Retry in ${Math.ceil(waitTime / 1000)}s`);
      }
    }

    this.totalCalls++;

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * 成功時の処理
   */
  private onSuccess(): void {
    this.lastSuccessTime = Date.now();
    this.totalSuccesses++;

    if (this.state === CircuitBreakerState.HALF_OPEN) {
      this.successCount++;
      if (this.successCount >= this.config.successThreshold) {
        this.reset();
        console.log('✅ Circuit Breaker: CLOSED状態に復旧');
      }
    } else {
      this.failureCount = 0; // CLOSED状態では失敗カウントをリセット
    }
  }

  /**
   * 失敗時の処理
   */
  private onFailure(): void {
    this.lastFailureTime = Date.now();
    this.failureCount++;
    this.totalFailures++;

    if (this.state === CircuitBreakerState.HALF_OPEN) {
      // HALF_OPEN中の失敗は即座にOPENに戻す
      this.state = CircuitBreakerState.OPEN;
      console.log('❌ Circuit Breaker: HALF_OPEN中の失敗により OPEN状態に戻る');
    } else if (this.failureCount >= this.config.failureThreshold) {
      this.state = CircuitBreakerState.OPEN;
      console.log(
        `⚠️ Circuit Breaker: 失敗回数が閾値(${this.config.failureThreshold})に達し OPEN状態に移行`
      );
    }
  }

  /**
   * リセット試行が可能かチェック
   */
  private canAttemptReset(): boolean {
    if (!this.lastFailureTime) return false;
    return Date.now() - this.lastFailureTime >= this.config.recoveryTimeout;
  }

  /**
   * 残り待機時間を取得
   */
  private getRemainingWaitTime(): number {
    if (!this.lastFailureTime) return 0;
    const elapsed = Date.now() - this.lastFailureTime;
    return Math.max(0, this.config.recoveryTimeout - elapsed);
  }

  /**
   * Circuit Breakerをリセット
   */
  private reset(): void {
    this.state = CircuitBreakerState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
  }

  /**
   * 統計情報を取得
   */
  getStats(): CircuitBreakerStats {
    return {
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureTime: this.lastFailureTime,
      lastSuccessTime: this.lastSuccessTime,
      totalCalls: this.totalCalls,
      totalFailures: this.totalFailures,
      totalSuccesses: this.totalSuccesses,
    };
  }

  /**
   * 手動でのリセット
   */
  manualReset(): void {
    console.log('🔧 Circuit Breaker: 手動リセット実行');
    this.reset();
  }

  /**
   * 現在の状態を取得
   */
  getState(): CircuitBreakerState {
    return this.state;
  }

  /**
   * 利用可能かチェック
   */
  isAvailable(): boolean {
    return this.state !== CircuitBreakerState.OPEN || this.canAttemptReset();
  }
}

/**
 * デフォルト設定でのCircuit Breaker作成
 */
export function createCircuitBreaker(customConfig?: Partial<CircuitBreakerConfig>): CircuitBreaker {
  const defaultConfig: CircuitBreakerConfig = {
    failureThreshold: 5, // 5回失敗で遮断
    recoveryTimeout: 60000, // 60秒待機
    monitoringPeriod: 30000, // 30秒監視期間
    successThreshold: 3, // 3回成功で復旧
  };

  const config = { ...defaultConfig, ...customConfig };
  return new CircuitBreaker(config);
}
